import { Logo } from "@paribus/shared-ui";
import { FileText, Menu, Plus, X } from "lucide-react";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import { useCases } from "../hooks/useCases";
import { canViewOwnPreference } from "../permissions/preferences";
import { useCanCreateCase } from "../permissions/v2/cases";
import { isSessionExpiredError } from "../services/apiService";
import "../styles/MobileLayout.css";
import { useNavigateToCustomizeReport } from "../utils";

const MobileChrome = ({ selectedCase, setSelectedCase, handleNewCase }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user, firmContext, signOut } = useAuth();
  const { cases: userCases, error: casesError, errorMessage: casesErrorMessage } = useCases();
  const canCreateCase = useCanCreateCase(firmContext?.firmId);
  const navigate = useNavigate();
  const goToCustomizeReport = useNavigateToCustomizeReport();

  useEffect(() => {
    // Cases already on screen mean only a background poll failed; don't interrupt for that.
    if (!casesError || isSessionExpiredError(casesError) || userCases.length > 0) return;
    console.error("Error fetching cases:", casesErrorMessage);
    toast.error("Failed to load cases. Please try refreshing the page.", {
      id: "cases-load-error",
    });
  }, [casesError, casesErrorMessage, userCases.length]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <>
      <header className="mobile-header">
        <button type="button" onClick={toggleSidebar} className="menu-button">
          {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <div className="header-center">
          <Logo />
        </div>
      </header>

      <div className={`mobile-sidebar ${isSidebarOpen ? "open" : ""}`}>
        <div className="case-selector">
          <select
            value={selectedCase}
            onChange={(e) => {
              const newCaseId = e.target.value;
              setSelectedCase(newCaseId);
              if (newCaseId) {
                navigate(`/cases/${newCaseId}/files`);
              }
              setIsSidebarOpen(false);
            }}
          >
            <option value="">Select case...</option>
            {userCases.map((c) => (
              <option key={c.case_id} value={c.case_id}>
                {c.case_name}
              </option>
            ))}
          </select>
        </div>
        <div className="case-actions">
          <button
            type="button"
            onClick={() => {
              handleNewCase();
              setIsSidebarOpen(false);
            }}
            disabled={!canCreateCase}
            title={canCreateCase ? undefined : "You don't have permission to create cases"}
            className="action-btn new-case-btn"
          >
            <Plus size={16} /> New Case
          </button>
          {selectedCase && (
            <Link
              to={`/cases/${selectedCase}/files`}
              className="action-btn manage-files-btn"
              onClick={() => setIsSidebarOpen(false)}
            >
              <FileText size={16} /> Manage Files
            </Link>
          )}
        </div>

        <div
          className="user-info"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <span style={{ marginBottom: "5px" }}>{user.email}</span>
          {canViewOwnPreference(firmContext) && (
            <button
              type="button"
              onClick={() => {
                setIsSidebarOpen(false);
                void goToCustomizeReport();
              }}
              className="clickable-btn"
              style={{ padding: 0, justifyContent: "flex-start" }}
            >
              Customize report
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              signOut();
              setIsSidebarOpen(false);
            }}
            className="clickable-btn"
          >
            Logout
          </button>
        </div>
      </div>
    </>
  );
};

MobileChrome.propTypes = {
  selectedCase: PropTypes.string,
  setSelectedCase: PropTypes.func.isRequired,
  handleNewCase: PropTypes.func.isRequired,
};

export default MobileChrome;
